import FileSaver from "file-saver";
import { inject, observer } from "mobx-react";
import React, { Component } from "react";
// import { Document, Page } from "react-pdf/dist/entry.webpack";
// import { Viewer, Worker, PageChangeEvent, ZoomEvent } from "@react-pdf-viewer/core";

// import "@react-pdf-viewer/core/lib/styles/index.css";
// import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import {
  BackgroundImage,
  Button,
  DynamicLink,
  P,
  FileTrackingForm,
} from "src/views/shared/components";
import { Icons } from "src/libs/assets";
import { tx } from "src/libs/i18n";
import { humanFileSize } from "src/libs/utils";
import { fallbackImage } from "src/libs/utils/fallbacks";
import {
  ISingleWordpressDocument,
  ISingleWordpressMediaItem,
} from "src/models";
import {
  AuthenticationStore,
  LibraryStore,
  LoginStore,
  NavigationStore,
  UIStore,
} from "src/stores";
import { AddDocumentToLibraryModal } from "src/views/shared/modals";
import * as s from "./PDFViewer.module.scss";
import { DocumentViewer } from "./DocumentViewer";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  pdf: ISingleWordpressMediaItem;
  document?: ISingleWordpressDocument;
  navigationStore?: NavigationStore;
  uiStore?: UIStore;
  authenticationStore?: AuthenticationStore;
  libraryStore?: LibraryStore;
  loginStore?: LoginStore;
  tracking?: boolean;
}

interface State {
  numPages: number;
  pageNumber: number;
  pageScale: number;
  pageElements: Array<any>;
}

const fileExtensionNicename = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "DOCX",
  "application/msword": "DOC",
  "application/vnd.ms-powerpoint": "PPT",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PPTX",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
};

@inject(
  "navigationStore",
  "uiStore",
  "authenticationStore",
  "libraryStore",
  "loginStore"
)
@observer
export class PDFViewer extends Component<Props, State> {
  private pageRefsCollection;
  private pdfViewerRef = React.createRef<any>();
  private pdfScrollWrapperRef = React.createRef<any>();

  constructor(props) {
    super(props);
    this.state = {
      numPages: 1,
      pageNumber: 1,
      pageScale: 1,
      pageElements: [],
    };

    this.pageRefsCollection = [];
  }

  print = (url: string) => {
    require("print-js")(url);
  };

  onDocumentLoadSuccess = ({ numPages }) => {
    this.setState({ numPages });
  };

  renderInformationItem = (item, key) => {
    const { label, value } = item;
    if (value === null || !value.length || value === undefined) {
      return;
    }
    if (label === tx("filter.authors")) {
      return (
        <div className={s.informationItem} key={key}>
          <P small className={s.informationItemLabel}>
            {label}
          </P>
          <P small className={s.informationItemValue}>
            <span
              {...{ "xmlns:cc": "http://creativecommons.org/ns#" }}
              property="cc:attributionName"
            >
              {value}
            </span>
          </P>
        </div>
      );
    }
    return (
      <div className={s.informationItem} key={key}>
        <P small className={s.informationItemLabel}>
          {label}
        </P>
        <P small className={s.informationItemValue}>
          {value}
        </P>
      </div>
    );
  };

  renderPageControls = () => {
    const { numPages, pageNumber } = this.state;
    const previousButtonClasses = [s.navigationButton, s.previousPageButton];
    const nextButtonClasses = [s.navigationButton, s.nextPageButton];

    if (pageNumber > 1) {
      previousButtonClasses.push(s.visible);
    }

    if (pageNumber < numPages) {
      nextButtonClasses.push(s.visible);
    }

    return (
      <div className={s.pageControlContainer}>
        {/* <div className={previousButtonClasses.join(" ")} onClick={this.previousPage}>
          <img
            src={Icons.chevronUp_small}
            alt="previous-page-icon"
            className={s.previousPageIcon}
          />
        </div> */}

        <div className={s.currentPageIndicator}>{pageNumber}</div>

        {/* <div className={nextButtonClasses.join(" ")} onClick={this.nextPage}>
          <img
            src={Icons.chevronDown_small}
            alt="next-page-icon"
            className={s.nextPageIcon}
          />
        </div> */}
      </div>
    );
  };

  pageIsInView = pageRect => {
    const pdfScrollWrapperRect = this.pdfScrollWrapperRef.current.getBoundingClientRect();
    const { height: wrapperHeight, top: wrapperTop } = pdfScrollWrapperRect;
    const { height, top } = pageRect;

    return height + Math.round(top) - wrapperTop <= wrapperHeight && top > 0;
  };

  pdfViewScrollHandler = el => {
    const { pageElements } = this.state;
    const pages = this.pdfViewerRef.current.pages;
    if (pageElements.length <= 0) {
      this.setState({ pageElements: pages });
    } else {
      pageElements.forEach(page => {
        let inView = this.pageIsInView(page.getBoundingClientRect());
        if (inView) {
          let elementPageNumber = parseInt(
            page.getAttribute("data-page-number")
          );
          if (this.state.pageNumber !== elementPageNumber) {
            this.setState({ pageNumber: elementPageNumber });
          }
        }
      });
    }
  };

  previousPage = () => {
    const { pageNumber, numPages } = this.state;
    if (pageNumber !== 1) {
      this.setState({ pageNumber: pageNumber - 1 });
    }
  };

  nextPage = () => {
    const { pageNumber, numPages } = this.state;
    if (pageNumber < numPages) {
      this.setState({ pageNumber: pageNumber + 1 });
    }
  };

  zoomIn = () => {
    const { pageScale } = this.state;
    this.setState({ pageScale: pageScale + 0.1 });
  };

  zoomOut = () => {
    const { pageScale } = this.state;
    if (pageScale > 1) {
      this.setState({ pageScale: pageScale - 0.1 });
    }
  };

  // onPageChange = (e: PageChangeEvent) => {
  //   this.setState({ pageNumber: e.currentPage + 1 });
  // };

  // onZoom = (e: ZoomEvent) => {
  //   this.setState({ pageScale: e.scale });
  // };

  renderInformationFields = () => {
    const { document } = this.props;
    const { documentFields } = document || {};
    if (!document || !documentFields) return <></>;
    const { format, publicationYear, rights, contributors = [] } =
      documentFields || {};
    const {
      documentTopics: { topics = [] },
      documentKeywords: { keywords = [] },
      documentPublishers: { publishers = [] },
      documentContentTypes: { contentTypes = [] },
      documentAuthors: { authors = [] },
    } = document;
    const infos = [
      { label: tx("filter.publicationYear"), value: publicationYear },
      {
        label: tx("filter.publisher"),
        value: publishers && publishers.map(i => i.name).join(", "),
      },
      { label: tx("filter.rights"), value: rights },
      {
        label: tx("filter.authors"),
        value: authors && authors.map(i => i.name).join(", "),
      },
      {
        label: tx("filter.contributors"),
        value: contributors && contributors.map(i => i.name).join(", "),
      },
      { label: tx("filter.format"), value: format },
      {
        label: tx("filter.contentType"),
        value: contentTypes && contentTypes.map(i => i.name).join(", "),
      },
      {
        label: tx("filter.topics"),
        value: topics && topics.map(i => i.name).join(", "),
      },

      {
        label: tx("filter.keywords"),
        value: keywords && keywords.map(i => i.name).join(", "),
      },
    ];

    return infos.map((item, i) => this.renderInformationItem(item, i));
  };

  toggleSaveToLibrary = (document: ISingleWordpressDocument) => {
    const { libraryStore } = this.props;
    libraryStore.onOpenModal(document);
  };

  render() {
    // if (typeof window === undefined) return <></>;
    const {
      pdf,
      navigationStore, // CHECK THIS OUT
      style,
      className,
      children,
      document,
      tracking,
      ...rest
    } = this.props;
    // const { pageNumber, numPages, pageScale } = this.state;
    const { mediaItemUrl, title, fileSize, mimeType } = pdf;
    const prettySize = fileSize ? humanFileSize(fileSize) : "";
    const { userHasValidToken } = this.props.authenticationStore;
    const { openLoginModal } = this.props.loginStore;
    const { isOpen } = this.props.libraryStore;

    const { isDesktop } = this.props.uiStore;

    const fileExtension = fileExtensionNicename[mimeType];

    let pdfViewerOverlayClasses = [s.pdfViewerContainer];
    if (!isDesktop) pdfViewerOverlayClasses.push(s.mobile);
    if (className) pdfViewerOverlayClasses.push(className);
    const documentURL = mediaItemUrl;
    const { documentThumbnail } = document;

    return (
      <div
        className={pdfViewerOverlayClasses.join(" ")}
        style={style}
        {...rest}
      >
        {!!tracking && (
          <FileTrackingForm
            databaseId={pdf?.databaseId}
            fileTitle={title}
            postId={document?.databaseId}
          />
        )}
        <div className={s.pdfContentContainer}>
          <DocumentViewer
            documentURL={documentURL}
            fileExtension={fileExtension}
          />
          <div className={s.documentInformationColumn}>
            <DynamicLink
              to={`${
                navigationStore.location ? navigationStore.location.origin : ""
              }/document/${document ? document.slug : ""}`}
              className={s.closeOverlayButton}
            >
              <img
                src={Icons.cancel_red_bg_white}
                alt="close-pdf-overlay"
                className={s.closeOverlayIcon}
              />
            </DynamicLink>
            <div className={s.downloadContainer}>
              <div className={s.documentImage}>
                <BackgroundImage
                  image={
                    (documentThumbnail && documentThumbnail.image) ||
                    fallbackImage("pdf")
                  }
                />
              </div>
              <h1
                {...{ "xmlns:dct": "http://purl.org/dc/terms/" }}
                property="dct:title"
                className={s.documentTitle}
              >
                {title}
              </h1>
              <div className={s.addToLibraryRow}>
                <P small className={s.fileInformation}>
                  {`${prettySize} (${fileExtension})`}
                </P>
                {isDesktop && (
                  <div
                    onClick={() => {
                      userHasValidToken
                        ? this.toggleSaveToLibrary(document)
                        : openLoginModal();
                    }}
                    data-hover={tx("icons.addToLibrary")}
                  >
                    <img
                      src={Icons.library_small}
                      alt={tx("icons.addToLibrary")}
                      className={s.toggleIcon}
                    />
                  </div>
                )}
              </div>
              <a
                href={documentURL}
                download
                onClick={e => {
                  e.preventDefault();
                  const split = documentURL.split("?")[0].split("/");
                  FileSaver.saveAs(documentURL, split[split.length - 1]);
                }}
              >
                <Button
                  className={s.downloadDocumentButton}
                  size="medium"
                  color="red"
                >
                  {tx("misc.download")}
                </Button>
              </a>
            </div>
            <div className={s.documentInformationContainer}>
              {this.renderInformationFields()}
            </div>
          </div>
        </div>
        {isDesktop && isOpen && <AddDocumentToLibraryModal />}
      </div>
    );
  }
}
